import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ThemeToggle from "@/components/feature/ThemeToggle";
import { analytics, site, navigation } from "@/content";

export default function AnalyticsNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // На /analytics якоря работают нативно; с других страниц — уводим на страницу
  const resolveHref = (href: string) => {
    if (href.startsWith("#")) {
      return pathname === "/analytics" ? href : `/analytics${href}`;
    }
    return href;
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 dark:bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-slate-200/80 dark:border-white/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          : "bg-white/70 dark:bg-[#0A0A0A]/60 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        {/* Логотип */}
        <Link
          to="/"
          className="flex items-center gap-2.5 cursor-pointer group shrink-0"
          aria-label={`${site.brand.name}${site.brand.suffix} — на главную`}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600 transition-all duration-300 group-hover:scale-110 group-hover:bg-blue-500">
            <i className="ri-bar-chart-box-line text-white text-sm"></i>
          </div>
          <span className="font-extrabold text-lg tracking-tight text-slate-900 dark:text-white transition-colors duration-300 group-hover:text-blue-700">
            {site.brand.name}
            <span className="text-blue-600 dark:text-blue-400 transition-colors duration-300 group-hover:text-blue-500">
              {site.brand.suffix}
            </span>
          </span>
        </Link>

        {/* Десктоп-меню */}
        <div className="hidden lg:flex items-center gap-6">
          {navigation.analytics.links.map((link) => (
            <a
              key={link.label}
              href={resolveHref(link.href)}
              className="text-sm font-medium text-slate-600 dark:text-white/65 hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <a
            href={site.contacts.phoneHref}
            className="hidden xl:inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 dark:text-white/65 hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-sm"></i>
            {site.contacts.phone}
          </a>
          <a
            href={resolveHref(navigation.analytics.cta.href)}
            className="text-sm font-bold px-5 py-2.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap shadow-sm shadow-blue-200"
          >
            {navigation.analytics.cta.label}
          </a>
        </div>

        {/* Бургер */}
        <button
          type="button"
          className="lg:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? "Закрыть меню" : "Открыть меню"}
          aria-expanded={menuOpen}
        >
          <i
            className={`${
              menuOpen ? "ri-close-line" : "ri-menu-line"
            } text-2xl text-slate-800 dark:text-white`}
          ></i>
        </button>
      </div>

      {/* Мобильное меню */}
      {menuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100 dark:border-white/[0.08] px-6 py-4 flex flex-col gap-4">
          {navigation.analytics.links.map((link) => (
            <a
              key={link.label}
              href={resolveHref(link.href)}
              onClick={() => setMenuOpen(false)}
              className="text-slate-700 dark:text-white/80 font-medium text-sm cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href={site.contacts.phoneHref}
            className="text-slate-500 dark:text-white/60 text-sm font-medium inline-flex items-center gap-1.5"
          >
            <i className="ri-phone-line"></i>
            {site.contacts.phone}
          </a>
          <div className="flex items-center gap-3 mt-1">
            <ThemeToggle />
            <a
              href={resolveHref(navigation.analytics.cta.href)}
              onClick={() => setMenuOpen(false)}
              className="flex-1 text-center text-sm font-bold px-5 py-3 rounded-full bg-blue-600 text-white cursor-pointer whitespace-nowrap"
            >
              {navigation.analytics.cta.label}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
